import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

/**
 * Platform-aware file download (PD-14606).
 *
 * In the browser, the classic invisible-anchor click triggers the normal
 * download flow. Inside the native Capacitor shell that same anchor click
 * silently does nothing — the WebView has no download manager — so the
 * user saw "preparing…" and then no result. On native we instead write
 * the blob to the app's cache directory and present the OS share sheet,
 * which offers Save to Files, AirDrop, print, etc., and is the standard
 * way iOS apps hand a document to the user.
 */
export async function saveOrShareBlob(
	blob: Blob,
	filename: string,
): Promise<void> {
	if (!Capacitor.isNativePlatform()) {
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = filename;
		a.click();
		window.URL.revokeObjectURL(url);
		return;
	}

	const base64 = await blobToBase64(blob);
	const written = await Filesystem.writeFile({
		path: filename,
		data: base64,
		directory: Directory.Cache,
	});
	await Share.share({
		title: filename,
		url: written.uri,
	});
}

function blobToBase64(blob: Blob): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onerror = (): void => {
			reject(new Error('Failed to read file data'));
		};
		reader.onload = (): void => {
			// result is a data URL ("data:<mime>;base64,<payload>") — the
			// Filesystem plugin wants only the base64 payload.
			const result = reader.result as string;
			const comma = result.indexOf(',');
			resolve(comma >= 0 ? result.slice(comma + 1) : result);
		};
		reader.readAsDataURL(blob);
	});
}
